import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import UserRole from '@careerstart/wae-common/src/main/constants/user-role';
import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { Box, Tooltip, Typography } from '@mui/material';

import { JOB_ORDER_TIME_STATUSES } from '../../constants/jobOrderConstants';
import selectUser from '../../store/selectors/appSelector';
import { BLACK, FEED_BACK, PRIMARY_COLOR, RED } from '../../theme/colorConstants';
import { PRIMARY_FONT } from '../../theme/fontConstants';
import useFairReliabilityScore from '../../utils/useFairReliabilityScore';
import { showSnackbar } from '../../views/app';
import CancelledJobMetrics from '../ShiftCard/JobView/subComponents/CancelledJobMetrics';

import {
  CLOCK_TONES,
  clocksAreEnabled,
  formatJobAddress,
  formatJobDate,
  formatShiftRange,
  FULFILLMENT_TONES,
  getClockRatios,
  getClockTone,
  getFillBarSegments,
  getFulfillmentPresentation,
  getOverstaffStats,
  getReliabilityScore,
} from './jobOrderCardModel';
import ReliabilityScoreRing from './ReliabilityScoreRing';

const TEXT_BLACK = '#000000';
const TEXT_MUTED = '#747487';

const FULFILLMENT_COLOR = {
  [FULFILLMENT_TONES.LOW]: RED[80],
  [FULFILLMENT_TONES.NEUTRAL]: BLACK[50],
  [FULFILLMENT_TONES.FULL]: FEED_BACK.GREEN_80,
};

const CLOCKED_IN_COLOR = {
  [CLOCK_TONES.DISABLED]: BLACK[40],
  [CLOCK_TONES.SHORT]: RED[80],
  [CLOCK_TONES.MET]: TEXT_BLACK,
};

const statLabelSx = {
  color: TEXT_MUTED,
  textTransform: 'uppercase',
  fontFamily: PRIMARY_FONT[400],
  fontSize: '12px',
  letterSpacing: '0.24px',
  lineHeight: '16px',
};

const statValueSx = {
  fontSize: '16px',
  lineHeight: '20px',
  color: TEXT_BLACK,
};

const splitShiftZone = (label) => {
  const parts = label.split(' ');
  const zone = parts[parts.length - 1];
  if (!zone || zone === 'AM' || zone === 'PM') {
    return { time: label, zone: '' };
  }
  return { time: parts.slice(0, -1).join(' '), zone };
};

const JobOrderCardFront = ({ jobOrder, timeStatus, isCancelled }) => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const isEmployer = user?.role === UserRole.EMPLOYER;
  const fulfillment = getFulfillmentPresentation(jobOrder?.fillRatio);
  const fillBarSegments = getFillBarSegments(jobOrder);
  const { pickReliability } = useFairReliabilityScore();
  const reliability = getReliabilityScore(
    pickReliability(jobOrder?.averageRosterRawReliability, jobOrder?.averageRosterReliability)
  );
  const overstaff = getOverstaffStats(jobOrder);
  const clocks = getClockRatios(jobOrder);
  const clocksEnabled = clocksAreEnabled({ timeStatus, isCancelled });
  const clockedInTone = getClockTone({
    numerator: clocks.clockedIn,
    denominator: clocks.clockedInDenominator,
    enabled: clocksEnabled,
  });
  const clockedOutTone = getClockTone({
    numerator: clocks.clockedOut,
    denominator: clocks.clockedOutDenominator,
    enabled: clocksEnabled,
  });
  const fulfillmentUsesBlack =
    timeStatus === JOB_ORDER_TIME_STATUSES.ACTIVE ||
    timeStatus === JOB_ORDER_TIME_STATUSES.COMPLETED;
  const fulfillmentColor =
    isEmployer || fulfillmentUsesBlack ? TEXT_BLACK : FULFILLMENT_COLOR[fulfillment.tone];
  const segmentFillColor = (tone) =>
    isEmployer ? FULFILLMENT_COLOR[FULFILLMENT_TONES.NEUTRAL] : FULFILLMENT_COLOR[tone];
  const clockedInColor = CLOCKED_IN_COLOR[clockedInTone];
  const clockedOutColor = clockedOutTone === CLOCK_TONES.DISABLED ? BLACK[40] : TEXT_BLACK;
  const address = formatJobAddress(jobOrder?.location);
  const shift = splitShiftZone(
    formatShiftRange(jobOrder?.start, jobOrder?.end, jobOrder?.timeZone)
  );
  const positionFulfillmentLabel = LanguageConverterV2('jobOrder.positionFulfillment');
  const placementsLabel = LanguageConverterV2('roster.form.placements');
  const openingsLabel = LanguageConverterV2('roster.form.openings');
  const overstaffLabel = LanguageConverterV2('jobOrder.overstaff');
  const clockedInLabel = LanguageConverterV2('jobOrder.clockedIn');
  const clockedOutLabel = LanguageConverterV2('jobOrder.clockedOut');
  const copyJobIdLabel = LanguageConverterV2('userCard.subTitle.copyJobId');
  const reliabilityTitle = LanguageConverterV2('jobOrder.averageReliabilityScore');
  const reliabilityDetail = LanguageConverterV2('jobOrder.averageReliabilityScoreDetail');

  const handleCopyJobId = async () => {
    if (!jobOrder?._id) return;
    try {
      await navigator.clipboard.writeText(jobOrder._id);
      dispatch(showSnackbar({ message: 'jobOrder.message.idCopied' }));
    } catch {
      dispatch(showSnackbar({ message: 'jobOrder.message.idCopyFailed' }));
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        textAlign: 'left',
        px: '12px',
      }}
    >
      <Box>
        <Typography
          sx={{
            fontFamily: PRIMARY_FONT[700],
            fontSize: '18px',
            lineHeight: '22px',
            color: TEXT_BLACK,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {jobOrder?.name}
        </Typography>
        {jobOrder?.corporation?.name ? (
          <Typography
            sx={{
              fontFamily: PRIMARY_FONT[400],
              fontSize: '14px',
              lineHeight: '18px',
              color: TEXT_MUTED,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {jobOrder.corporation.name}
          </Typography>
        ) : null}
        <Box
          component="button"
          type="button"
          onClick={handleCopyJobId}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            mt: 0.5,
            p: 0,
            border: 0,
            background: 'transparent',
            cursor: 'pointer',
            color: TEXT_MUTED,
            fontFamily: PRIMARY_FONT[500],
            fontSize: '12px',
            lineHeight: '16px',
          }}
        >
          <ContentCopyOutlinedIcon sx={{ fontSize: 12 }} />
          {copyJobIdLabel}
        </Box>
      </Box>

      {isCancelled ? (
        <CancelledJobMetrics jobOrder={jobOrder} />
      ) : (
        <>
          <Box>
            <Typography sx={statLabelSx}>{positionFulfillmentLabel}</Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
              }}
            >
              <Typography
                data-testid="job-order-card-fulfillment"
                data-tone={fulfillment.tone}
                sx={{
                  ...statValueSx,
                  fontFamily: PRIMARY_FONT[700],
                  color: fulfillmentColor,
                }}
              >
                {`${fulfillment.percent}%`}
              </Typography>
              <Tooltip
                title={
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>
                      {reliabilityTitle}
                    </Typography>
                    <Typography sx={{ fontSize: '12px' }}>{reliabilityDetail}</Typography>
                  </Box>
                }
              >
                <Box
                  data-testid="job-order-card-reliability"
                  data-tone={reliability?.tone || 'empty'}
                  tabIndex={0}
                  sx={{
                    flexShrink: 0,
                    borderRadius: '50%',
                    '&:focus-visible': {
                      outline: `2px solid ${BLACK[50]}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  <ReliabilityScoreRing value={reliability?.value} />
                </Box>
              </Tooltip>
            </Box>
            <Box
              data-testid="job-order-card-fill-bar"
              sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: 0.75 }}
            >
              {fillBarSegments.map((segment) => (
                <Box
                  key={segment.key}
                  data-testid={`job-order-card-fill-bar-${segment.key}`}
                  data-fill={Math.round(segment.fill * 100)}
                  data-tone={segment.tone}
                  sx={{
                    flex: `${segment.weight} 1 0`,
                    minWidth: segment.key === 'overstaff' ? 12 : 0,
                    height: segment.key === 'overstaff' ? 8 : 14,
                    borderRadius: 20,
                    backgroundColor: BLACK[20],
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${Math.min(100, Math.max(0, segment.fill * 100))}%`,
                      height: '100%',
                      borderRadius: 20,
                      backgroundColor: segmentFillColor(segment.tone),
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
            <Box>
              <Typography sx={statLabelSx}>{placementsLabel}</Typography>
              <Typography sx={{ ...statValueSx, fontFamily: PRIMARY_FONT[700] }}>
                {jobOrder?.placementsCount ?? 0}
              </Typography>
            </Box>
            <Box>
              <Typography sx={statLabelSx}>{openingsLabel}</Typography>
              <Typography sx={{ ...statValueSx, fontFamily: PRIMARY_FONT[500], color: BLACK[100] }}>
                {jobOrder?.numOpenings ?? 0}
              </Typography>
            </Box>
            <Box>
              <Typography sx={statLabelSx}>{overstaffLabel}</Typography>
              <Box
                data-testid="job-order-card-overstaff"
                sx={{ display: 'flex', alignItems: 'flex-end', gap: '4px' }}
              >
                <Typography
                  sx={{ ...statValueSx, fontFamily: PRIMARY_FONT[500], color: BLACK[100] }}
                >
                  {`${overstaff.filled}/${overstaff.total}`}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: PRIMARY_FONT[400],
                    fontSize: '11px',
                    lineHeight: '16px',
                    color: TEXT_MUTED,
                  }}
                >
                  {`(${overstaff.percent}%)`}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: '48px', textAlign: 'center' }}>
            <Box>
              <Typography sx={{ ...statLabelSx, color: clockedInColor }}>
                {clockedInLabel}
              </Typography>
              <Typography
                data-testid="job-order-card-clocked-in"
                data-tone={clockedInTone}
                sx={{ ...statValueSx, color: clockedInColor, fontFamily: PRIMARY_FONT[700] }}
              >
                {`${clocks.clockedIn}/${clocks.clockedInDenominator}`}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ ...statLabelSx, color: clockedOutColor }}>
                {clockedOutLabel}
              </Typography>
              <Typography
                data-testid="job-order-card-clocked-out"
                data-tone={clockedOutTone}
                sx={{ ...statValueSx, color: clockedOutColor, fontFamily: PRIMARY_FONT[700] }}
              >
                {`${clocks.clockedOut}/${clocks.clockedOutDenominator}`}
              </Typography>
            </Box>
          </Box>
        </>
      )}

      {!isCancelled ? (
        <Box
          sx={{ borderTop: `1px solid ${BLACK[30]}` }}
          data-testid="job-order-card-time-divider"
        />
      ) : null}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', color: BLACK[70] }}>
          <AccessTimeOutlinedIcon sx={{ fontSize: 16 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography
              data-testid="job-order-card-shift-time"
              sx={{ fontFamily: PRIMARY_FONT[400], fontSize: '14px', lineHeight: '18px' }}
            >
              {shift.time}
            </Typography>
            {shift.zone ? (
              <Typography
                data-testid="job-order-card-shift-zone"
                sx={{
                  fontFamily: PRIMARY_FONT[500],
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: '0.24px',
                  color: PRIMARY_COLOR[70],
                }}
              >
                {shift.zone}
              </Typography>
            ) : null}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', color: BLACK[70] }}>
          <CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Typography
              sx={{ fontFamily: PRIMARY_FONT[400], fontSize: '14px', lineHeight: '18px' }}
            >
              {formatJobDate(jobOrder?.start, jobOrder?.timeZone)}
            </Typography>
            <Typography
              sx={{
                fontFamily: PRIMARY_FONT[500],
                fontSize: '14px',
                lineHeight: '18px',
                color: TEXT_BLACK,
              }}
            >
              -
            </Typography>
            <Typography
              sx={{ fontFamily: PRIMARY_FONT[400], fontSize: '14px', lineHeight: '18px' }}
            >
              {formatJobDate(jobOrder?.end, jobOrder?.timeZone)}
            </Typography>
          </Box>
        </Box>
        {address ? (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px', color: BLACK[70] }}>
            <LocationOnOutlinedIcon sx={{ fontSize: 16 }} />
            <Typography
              sx={{ fontFamily: PRIMARY_FONT[400], fontSize: '14px', lineHeight: '18px' }}
            >
              {address}
            </Typography>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

JobOrderCardFront.propTypes = {
  jobOrder: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    start: PropTypes.number,
    end: PropTypes.number,
    timeZone: PropTypes.string,
    fillRatio: PropTypes.number,
    averageRosterReliability: PropTypes.number,
    averageRosterRawReliability: PropTypes.number,
    numOpenings: PropTypes.number,
    numOverstaff: PropTypes.number,
    placementsCount: PropTypes.number,
    placements: PropTypes.arrayOf(PropTypes.shape({})),
    corporation: PropTypes.shape({ name: PropTypes.string }),
    location: PropTypes.shape({
      address: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zipcode: PropTypes.string,
    }),
  }),
  timeStatus: PropTypes.string,
  isCancelled: PropTypes.bool,
};

export default JobOrderCardFront;
